package com.rainbowclubs.rainbow.presentation.admin

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.requests.UpdateEventRequest
import com.rainbowclubs.rainbow.domain.use_cases.events.DeleteEventUseCase
import com.rainbowclubs.rainbow.domain.use_cases.events.GetClubEventsUseCase
import com.rainbowclubs.rainbow.domain.use_cases.events.UpdateEventUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.event_delete_error
import rainbowclubs.composeapp.generated.resources.event_form_error

class UpdateEventViewModel(
    private val clubId: String,
    private val eventId: String,
    private val getClubEventsUseCase: GetClubEventsUseCase,
    private val updateEventUseCase: UpdateEventUseCase,
    private val deleteEventUseCase: DeleteEventUseCase,
) : ViewModel() {

    sealed class NavigationEvent {
        object Back : NavigationEvent()
        object EventDeleted : NavigationEvent()
    }

    private val _screenState = MutableStateFlow<ScreenState<EventFormUiState>>(ScreenState.Loading)
    val screenState: StateFlow<ScreenState<EventFormUiState>> = _screenState.asStateFlow()

    private val navigationIntentChannel = Channel<NavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<NavigationEvent> = navigationIntentChannel.receiveAsFlow()

    init {
        fetchEvent()
    }

    fun fetchEvent() {
        viewModelScope.launch {
            _screenState.value = ScreenState.Loading
            getClubEventsUseCase(
                clubId = clubId,
                page = 1,
                limit = 100,
            ).onSuccess { response ->
                val event = response.events.firstOrNull { it.id == eventId }
                if (event == null) {
                    _screenState.value = ScreenState.Error(IllegalStateException("Event not found"))
                } else {
                    _screenState.value = ScreenState.Content(
                        EventFormUiState().withEventValues(
                            title = event.title,
                            description = event.description,
                            location = event.location,
                            startsAt = event.startsAt,
                            endsAt = event.endsAt,
                        )
                    )
                }
            }.onFailure {
                _screenState.value = ScreenState.Error(it)
            }
        }
    }

    fun onTitleChanged(value: String) {
        updateForm {
            it.copy(
                title = value,
                titleError = null,
                validationError = null,
                formError = null,
                deleteError = null,
            )
        }
    }

    fun onDescriptionChanged(value: String) {
        updateForm {
            it.copy(
                description = value,
                descriptionError = null,
                validationError = null,
                formError = null,
                deleteError = null,
            )
        }
    }

    fun onLocationChanged(value: String) {
        updateForm { it.copy(location = value, validationError = null, formError = null, deleteError = null) }
    }

    fun onDateChanged(value: String) {
        updateForm {
            it.copy(
                date = value,
                dateError = null,
                validationError = null,
                formError = null,
                deleteError = null,
            )
        }
    }

    fun onEndDateChanged(value: String) {
        updateForm {
            it.copy(
                endDate = value,
                endDateError = null,
                validationError = null,
                formError = null,
                deleteError = null,
            )
        }
    }

    fun onStartsAtChanged(value: String) {
        updateForm {
            it.copy(
                startsAt = value,
                startsAtError = null,
                validationError = null,
                formError = null,
                deleteError = null,
            )
        }
    }

    fun onEndsAtChanged(value: String) {
        updateForm {
            it.copy(
                endsAt = value,
                endsAtError = null,
                validationError = null,
                formError = null,
                deleteError = null,
            )
        }
    }

    fun saveChanges() {
        val form = (_screenState.value as? ScreenState.Content)?.data ?: return
        val validation = validateEventForm(form)
        val timestamps = validation.timestamps
        if (timestamps == null) {
            _screenState.value = ScreenState.Content(validation.form)
            return
        }

        viewModelScope.launch {
            _screenState.value = ScreenState.Content(validation.form.copy(isSubmitting = true))

            updateEventUseCase(
                eventId = eventId,
                request = UpdateEventRequest(
                    startsAt = timestamps.startsAt,
                    endsAt = timestamps.endsAt,
                    description = validation.form.description.trim(),
                    title = validation.form.title.trim(),
                    location = validation.form.location.trim().ifBlank { null },
                ),
            ).onSuccess {
                navigationIntentChannel.send(NavigationEvent.Back)
            }.onFailure {
                _screenState.value = ScreenState.Content(
                    validation.form.copy(
                        formError = Res.string.event_form_error,
                        isSubmitting = false,
                    )
                )
            }
        }
    }

    fun clearDeleteError() {
        updateForm { it.copy(deleteError = null) }
    }

    fun deleteEvent() {
        val form = (_screenState.value as? ScreenState.Content)?.data ?: return
        if (form.isDeleting) return

        val deletingForm = form.copy(
            isDeleting = true,
            deleteError = null,
            formError = null,
        )
        _screenState.value = ScreenState.Content(deletingForm)

        viewModelScope.launch {
            deleteEventUseCase(eventId)
                .onSuccess {
                    navigationIntentChannel.send(NavigationEvent.EventDeleted)
                }
                .onFailure {
                    _screenState.value = ScreenState.Content(
                        deletingForm.copy(
                            isDeleting = false,
                            deleteError = Res.string.event_delete_error,
                        )
                    )
                }
        }
    }

    private fun updateForm(update: (EventFormUiState) -> EventFormUiState) {
        val form = (_screenState.value as? ScreenState.Content)?.data ?: return
        _screenState.value = ScreenState.Content(update(form))
    }
}
